import { getUsuarioLogadoAdaptado as getUsuarioLogado } from "../../utils/ids";
import Swal from "sweetalert2";
import { usePermissaoEventos } from "../../hooks/usePermissaoEventos";
import { useDisciplinaAtual } from "../../hooks/useDisciplinaAtual";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pencil, Search, Filter, Plus } from "lucide-react";
import "./EventosPublicados.scss";
import { getEventos } from "../../services/eventosStore";
import ModalConfirmacoes from "../../components/funcionalidadesEvento/ModalConfirmacoes";
import {
  getTurmas,
  recarregarSeNecessario as recarregarTurmas,
} from "../../services/turmasStore";
import {
  getUsuarios,
  getNomeUsuario,
  recarregarSeNecessario as recarregarUsuarios,
} from "../../services/usuariosStore";
import {
  parseDateLocal,
  formatDatePtBR as formatarDataPtBR,
} from "../../utils/formatting";

function getDisciplinaAtualId() {
  return localStorage.getItem("disciplinaAtualId") || "";
}

function normStr(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function getTipoUsuario(u) {
  const raw = u?.tipo ?? u?.cargo ?? u?.perfil ?? "";
  return String(raw).toLowerCase().trim();
}

function toIdStr(v) {
  const s = String(v ?? "").trim();
  return s || null;
}

// Label da turma = "Nome - Complemento" (ex: "Cálculo 1 - Turma 9").
// Sem complemento, mostra só o nome. Fallback "Turma {id}" se não achar.
function labelTurma(turma, id) {
  if (!turma) return `Turma ${id}`;
  const nome = String(turma.nome || "").trim();
  const comp = String(turma.complemento || "").trim();
  if (!nome) return `Turma ${id}`;
  return comp ? `${nome} - ${comp}` : nome;
}

function getTurmasDoEvento(ev) {
  if (Array.isArray(ev?.disciplinaIds) && ev.disciplinaIds.length > 0) {
    return ev.disciplinaIds.map(toIdStr).filter(Boolean);
  }

  if (Array.isArray(ev?.turmasIds) && ev.turmasIds.length > 0) {
    return ev.turmasIds.map(toIdStr).filter(Boolean);
  }

  if (Array.isArray(ev?.disciplinaId)) {
    const arr = ev.disciplinaId.map(toIdStr).filter(Boolean);
    if (arr.length) return arr;
  }

  const candidates = [
    ev?.disciplinaCoordenadorId,
    ev?.disciplinacoordenadorId,
    ev?.disciplinCoordenadoraId,
    ev?.disciplinaCoordenadorID,
    ev?.disciplina_coordenador_id,
    ev?.disciplicoordenadornaId,
  ].filter((v) => v != null);

  for (const v of candidates) {
    if (Array.isArray(v)) {
      const arr = v.map(toIdStr).filter(Boolean);
      if (arr.length) return arr;
    } else {
      const s = toIdStr(v);
      if (s) return [s];
    }
  }

  const single = toIdStr(ev?.disciplinaId);
  return single ? [single] : [];
}

function CardCoordenador({
  ev,
  criadoPor,
  dataAtual,
  temDataEvento,
  dataEventoFmt,
  podeEditar,
  eventoPassado,
  handleEditar,
  temCalendario,
  temDestaque,
  turmasOrdenadas,
  mostrarTodasTurmas,
  temConfirmacaoPresenca,
  onVerConfirmacoes,
}) {
  return (
    <article className="evento-card evento-card--coord">
      <div className="evento-card-top">
        <div className="evento-card-titleblock">
          <div className="evento-card-createdbyline">
            Criado por: {criadoPor}
          </div>

          <div className="evento-card-title">{ev.titulo}</div>

          <div className="evento-card-desc">{ev.descricao}</div>

          {turmasOrdenadas.length > 0 && (
            <div className="evento-card-turmaschips">
              {(mostrarTodasTurmas
                ? turmasOrdenadas
                : turmasOrdenadas.slice(0, 1)
              ).map((t, idx) => (
                <span key={`${t.id}-${idx}`} className="chip chip--turma">
                  {t.nome}
                </span>
              ))}
            </div>
          )}

          <div className="evento-card-meta">
            <div className="evento-card-dates">
              {temDataEvento && <span>Data do evento: {dataEventoFmt}</span>}

              <div className="evento-card-lastline">
                <span>Última atualização: {dataAtual}</span>

                {(temCalendario || temDestaque) && (
                  <div className="evento-card-chips">
                    {temCalendario && (
                      <span className="chip chip--calendario">Calendário</span>
                    )}
                    {temDestaque && (
                      <span className="chip chip--destaque">Destaque</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {temConfirmacaoPresenca && (
          <button
            type="button"
            className="btn-confirmacoes"
            onClick={onVerConfirmacoes}
            aria-label="Ver confirmações de presença"
            title="Confirmações de presença"
          >
            <Plus size={16} />
          </button>
        )}

        {podeEditar && (
          <button
            type="button"
            className={`btn-editar-icononly ${eventoPassado ? "is-disabled" : ""}`}
            onClick={() => handleEditar(ev.id, ev)}
            disabled={eventoPassado}
            aria-label={
              eventoPassado
                ? "Edição bloqueada (evento já ocorreu)"
                : "Editar evento"
            }
            title={
              eventoPassado
                ? "Não é possível editar eventos com data anterior"
                : "Editar"
            }
          >
            <Pencil size={16} />
          </button>
        )}
      </div>
    </article>
  );
}

function CardDefault({
  ev,
  criadoPor,
  dataAtual,
  temDataEvento,
  dataEventoFmt,
  podeEditar,
  eventoPassado,
  handleEditar,
  temCalendario,
  temDestaque,
  turmasOrdenadas,
  temConfirmacaoPresenca,
  onVerConfirmacoes,
}) {
  return (
    <article className="evento-card evento-card--default">
      <div className="evento-default-top">
        <div className="evento-default-left">
          <div className="evento-default-title">{ev.titulo}</div>

          <div className="evento-default-updatedBottom">
            <span className="evento-default-footer-label">Criado por:</span>{" "}
            <span className="evento-default-footer-value">{criadoPor}</span>
          </div>

          {turmasOrdenadas?.length > 0 && (
            <div className="chips-row">
              {turmasOrdenadas.slice(0, 2).map((t, idx) => (
                <span key={`${t.id}-${idx}`} className="chip chip--turma">
                  {t.nome}
                </span>
              ))}
            </div>
          )}

          {(temCalendario || temDestaque) && (
            <div className="evento-default-chips">
              {temCalendario && (
                <span className="chip chip--calendario">Calendário</span>
              )}
              {temDestaque && (
                <span className="chip chip--destaque">Destaque</span>
              )}
            </div>
          )}
        </div>

        {temConfirmacaoPresenca && (
          <button
            type="button"
            className="btn-confirmacoes"
            onClick={onVerConfirmacoes}
            aria-label="Ver confirmações de presença"
            title="Confirmações de presença"
          >
            <Plus size={16} />
          </button>
        )}

        {podeEditar && (
          <button
            type="button"
            className={`btn-editar-text ${eventoPassado ? "is-disabled" : ""}`}
            onClick={() => handleEditar(ev.id, ev)}
            disabled={eventoPassado}
            title={
              eventoPassado
                ? "Não é possível editar eventos com data anterior"
                : "Editar"
            }
          >
            <Pencil size={14} />
            <span>Editar</span>
          </button>
        )}
      </div>

      {ev.descricao ? (
        <div className="evento-default-desc">{ev.descricao}</div>
      ) : null}

      <div
        className={`evento-default-footer ${
          temDataEvento
            ? "evento-default-footer--with-date"
            : "evento-default-footer--no-date"
        }`}
      >
        {temDataEvento ? (
          <>
            <div className="evento-default-eventdate">
              <span className="evento-default-footer-label">
                Data do evento:
              </span>{" "}
              {dataEventoFmt}
            </div>

            <div className="evento-default-updatedBottom">
              <span className="evento-default-footer-label">
                Última atualização:
              </span>{" "}
              {dataAtual}
            </div>
          </>
        ) : (
          <div className="evento-default-updatedBottom">
            <span className="evento-default-footer-label">
              Última atualização:
            </span>{" "}
            {dataAtual}
          </div>
        )}
      </div>
    </article>
  );
}

export default function EventosPublicados() {
  const [usuarioLogado, setUsuarioLogado] = useState(() => getUsuarioLogado());
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const turmaIdParam = searchParams.get("turmaId");

  // Query param ?turmaId tem prioridade sobre o filtro persistido em
  // localStorage. Permite linkar direto para eventos de uma turma específica
  // (Dashboard → card de turma → Eventos).
  const [disciplinaAtualId, setDisciplinaAtualId] = useState(
    () => turmaIdParam || getDisciplinaAtualId(),
  );

  useEffect(() => {
    if (turmaIdParam && turmaIdParam !== disciplinaAtualId) {
      setDisciplinaAtualId(turmaIdParam);
    }
  }, [turmaIdParam, disciplinaAtualId]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busca, setBusca] = useState("");
  const [apenasCoordenadores, setApenasCoordenadores] = useState(false);
  const [eventoConfirmacoesId, setEventoConfirmacoesId] = useState(null);
  const [turmasCache, setTurmasCache] = useState(() => getTurmas());
  const [usuariosCache, setUsuariosCache] = useState(() => getUsuarios());
  // Coordenação selecionada no menu lateral (coordenador com 2+ coordenações
  // filtra eventos pela coordenação atual).
  const [coordenacaoAtualId, setCoordenacaoAtualId] = useState(
    () => localStorage.getItem("coordenacaoAtualId") || "",
  );

  const instituicaoId = useMemo(
    () => usuarioLogado?.faculdadeId || usuarioLogado?.instituicaoId || null,
    [usuarioLogado],
  );

  useEffect(() => {
    const onDisciplinaChanged = () =>
      setDisciplinaAtualId(getDisciplinaAtualId());

    const onStorage = (e) => {
      if (e.key === "usuario") setUsuarioLogado(getUsuarioLogado());
      if (e.key === "disciplinaAtualId") {
        setDisciplinaAtualId(getDisciplinaAtualId());
      }
      if (e.key === "coordenacaoAtualId") {
        setCoordenacaoAtualId(localStorage.getItem("coordenacaoAtualId") || "");
      }
    };

    const onEventosChanged = () => setRefreshKey((k) => k + 1);
    const onTurmasChanged = () => setTurmasCache(getTurmas(instituicaoId));
    const onUsuariosChanged = () =>
      setUsuariosCache(getUsuarios(instituicaoId));
    const onCoordChanged = () =>
      setCoordenacaoAtualId(localStorage.getItem("coordenacaoAtualId") || "");

    window.addEventListener("disciplinaAtual:changed", onDisciplinaChanged);
    window.addEventListener("storage", onStorage);
    window.addEventListener("eventos:changed", onEventosChanged);
    window.addEventListener("turmas:changed", onTurmasChanged);
    window.addEventListener("usuarios:changed", onUsuariosChanged);
    window.addEventListener("coordenacaoAtual:changed", onCoordChanged);

    if (instituicaoId) {
      recarregarTurmas(instituicaoId).catch(() => {});
      recarregarUsuarios(instituicaoId).catch(() => {});
    }

    return () => {
      window.removeEventListener(
        "disciplinaAtual:changed",
        onDisciplinaChanged,
      );
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("eventos:changed", onEventosChanged);
      window.removeEventListener("turmas:changed", onTurmasChanged);
      window.removeEventListener("usuarios:changed", onUsuariosChanged);
      window.removeEventListener("coordenacaoAtual:changed", onCoordChanged);
    };
  }, [instituicaoId]);

  const usuariosById = useMemo(() => {
    const map = new Map();
    usuariosCache.forEach((u) => map.set(String(u.id), u));
    return map;
  }, [usuariosCache]);

  const disciplinasById = useMemo(() => {
    const map = new Map();
    turmasCache.forEach((t) => map.set(String(t.id), t));
    return map;
  }, [turmasCache]);

  const tipo = useMemo(() => {
    return String(usuarioLogado?.tipo || "")
      .toLowerCase()
      .trim();
  }, [usuarioLogado]);

  const isCoordenador = tipo === "coordenador";
  const isProfessor = tipo === "professor";
  const isResponsavel = tipo === "responsavel";
  const isAluno = tipo === "aluno";

  const userTurmasSet = useMemo(() => {
    const ids =
      (Array.isArray(usuarioLogado?.disciplinas) &&
        usuarioLogado.disciplinas) ||
      [];
    return new Set(ids.map((v) => String(v ?? "").trim()).filter(Boolean));
  }, [usuarioLogado]);

  // Bloqueio da página inteira por permissão (mesmo padrão de Criar Evento):
  // responsável/aluno-responsável sem podeCriarEditarEventos na turma atual vê
  // a página toda esmaecida + aviso. Coordenador sempre pode (hook retorna true).
  const turmaContexto = useDisciplinaAtual();
  const { pode: podeEditarNaTurma } = usePermissaoEventos(turmaContexto);
  const bloqueado = !isCoordenador && !podeEditarNaTurma;

  const avisarBloqueio = () => {
    Swal.fire({
      title: "Ação não permitida",
      text: "Este usuário não pode efetuar essa ação.",
      icon: "info",
      confirmButtonText: "Entendi",
      confirmButtonColor: "#2E4A67",
    });
  };

  // Turmas da coordenação selecionada no menu. Coordenador com 2+ coordenações:
  // só vê eventos das turmas da coordenação atual. Sem coordenação selecionada
  // (null) → todas as turmas dele.
  const turmasDaCoordenacaoAtual = useMemo(() => {
    if (!coordenacaoAtualId) return null;
    const set = new Set();
    turmasCache.forEach((t) => {
      const coordIds = Array.isArray(t?.coordenacaoIds) ? t.coordenacaoIds : [];
      if (coordIds.some((id) => String(id) === String(coordenacaoAtualId))) {
        set.add(String(t.id));
      }
    });
    return set;
  }, [coordenacaoAtualId, turmasCache]);

  const isEventoCriadoPorCoordenador = useCallback(
    (ev) => String(ev?.criadoPorId) === String(usuarioLogado?.id),
    [usuarioLogado],
  );

  useEffect(() => {
    if (!isCoordenador) {
      setBusca("");
      setApenasCoordenadores(false);
    }
  }, [isCoordenador]);

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  function isDataEventoPassada(dataEvento) {
    if (!dataEvento) return false;
    const dt = parseDateLocal(dataEvento);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return startOfDay(dt).getTime() < startOfDay(new Date()).getTime();
  }

  const eventosFiltrados = useMemo(() => {
    if (!usuarioLogado) return [];
    if (refreshKey < 0) return [];

    const instId = String(
      usuarioLogado?.faculdadeId ?? usuarioLogado?.instituicaoId ?? "",
    );
    const discAtualId = disciplinaAtualId
      ? String(disciplinaAtualId).trim()
      : null;

    const eventosFinal = getEventos([]);

    const q = normStr(busca);

    return eventosFinal
      .filter(
        (ev) =>
          !instId ||
          String(ev.instituicaoId || "") === instId ||
          !ev.instituicaoId,
      )
      .filter((ev) => {
        if (!isCoordenador) return true;
        if (!apenasCoordenadores) return true;
        return isEventoCriadoPorCoordenador(ev);
      })
      .filter((ev) => {
        const turmasEv = getTurmasDoEvento(ev);

        if (isCoordenador) {
          // Filtra pela coordenação selecionada no menu (se houver). Coordenador
          // com 2+ coordenações vê só os eventos das turmas da coordenação atual.
          if (turmasDaCoordenacaoAtual) {
            return turmasEv.some((id) =>
              turmasDaCoordenacaoAtual.has(String(id)),
            );
          }
          // Sem coordenação selecionada: todas as turmas dele.
          if (!userTurmasSet || userTurmasSet.size === 0) return true;
          return turmasEv.some((id) => userTurmasSet.has(String(id)));
        }

        if (isProfessor || isResponsavel || isAluno) {
          if (!discAtualId) return true;
          return turmasEv.includes(discAtualId);
        }

        if (!discAtualId) return true;
        return turmasEv.includes(discAtualId);
      })
      .filter((ev) => {
        if (!q) return true;

        const criadoPor =
          ev.criadoPorNome || getNomeUsuario(ev.criadoPorId, "");
        const turmasEv = getTurmasDoEvento(ev);

        const nomesTurmas = (turmasEv || [])
          .map((id) => labelTurma(disciplinasById.get(String(id)), id))
          .join(" ");

        const hay = normStr(
          `${ev.titulo} ${ev.descricao} ${criadoPor} ${nomesTurmas}`,
        );

        return hay.includes(q);
      })
      .sort((a, b) => {
        const ta = new Date(a.ultimaAtualizacao || 0).getTime();
        const tb = new Date(b.ultimaAtualizacao || 0).getTime();
        return tb - ta;
      });
  }, [
    usuarioLogado,
    disciplinaAtualId,
    refreshKey,
    busca,
    isCoordenador,
    isProfessor,
    isResponsavel,
    isAluno,
    disciplinasById,
    userTurmasSet,
    turmasDaCoordenacaoAtual,
    apenasCoordenadores,
    isEventoCriadoPorCoordenador,
  ]);

  const handleEditar = (eventoId, ev) => {
    if (isDataEventoPassada(ev?.dataEvento)) return;
    navigate(`/eventos/${eventoId}/editar`);
  };

  const topoCoordenador = (
    <section className="card-bloco criar-evento-card eventos-publicados-topo-coordenador">
      <div className="eventos-publicados-topo-coordenador__header">
        <h1 className="page-title">Eventos publicados</h1>
      </div>

      <div className="eventos-publicados-topo-coordenador__filtros">
        <div className="filtros-top">
          <div className="filtros-title">
            <Filter size={16} className="filtros-icon" aria-hidden="true" />
            Filtros
          </div>

          <button
            type="button"
            className={`filtros-pill ${apenasCoordenadores ? "is-on" : ""}`}
            onClick={() => setApenasCoordenadores((v) => !v)}
            aria-pressed={apenasCoordenadores}
            title="Apenas eventos criados por coordenadores"
          >
            Selecionar apenas meus eventos
            <span
              className={`filtros-pill-dot ${
                apenasCoordenadores ? "is-filled" : "is-empty"
              }`}
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="filtros-row">
          <div className="filtro-search">
            <Search
              size={16}
              className="filtro-search__icon"
              aria-hidden="true"
            />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, disciplina ou professor..."
            />
          </div>
        </div>
      </div>
    </section>
  );

  const topoDefault = (
    <header className="eventos-publicados-header">
      <h1>Eventos publicados</h1>
      <p>Consulte, gerencie e acompanhe todos os eventos já publicados</p>
    </header>
  );

  if (!usuarioLogado) {
    return (
      <div
        className={`eventos-publicados-page ${
          isCoordenador ? "eventos-publicados-page--coordenador" : ""
        }`}
      >
        <div
          className={`eventos-publicados-container ${
            isCoordenador ? "eventos-publicados-container--coordenador" : ""
          }`}
        >
          {isCoordenador ? topoCoordenador : topoDefault}

          <div className="eventos-publicados-empty">
            Usuário não identificado. Faça login novamente.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`eventos-publicados-page ${
        isCoordenador ? "eventos-publicados-page--coordenador" : ""
      } ${bloqueado ? "eventos-publicados-page--bloqueado" : ""}`}
    >
      {bloqueado && (
        <div
          className="permissao-overlay"
          onClick={avisarBloqueio}
          role="button"
          tabIndex={0}
          aria-label="Este usuário não pode efetuar essa ação."
        />
      )}
      <div
        className={`eventos-publicados-container ${
          isCoordenador ? "eventos-publicados-container--coordenador" : ""
        }`}
      >
        {isCoordenador ? topoCoordenador : topoDefault}

        <div className="eventos-publicados-list-wrap">
          <div className="eventos-publicados-list">
            {eventosFiltrados.map((ev) => {
              // Nome do criador: prefere o que vem do backend (dinâmico); cai no
              // cache local só se o backend não enviar.
              const criadoPor =
                ev.criadoPorNome || getNomeUsuario(ev.criadoPorId, "—");

              const dataAtual = formatarDataPtBR(ev.ultimaAtualizacao);
              const temCalendario = !!ev.calendario;
              // Data do evento só existe quando tem calendário. Destaque-only
              // grava data default (0001/lixo) no backend — não exibir.
              const temDataEvento = temCalendario && !!ev.dataEvento;
              const dataEventoFmt = temDataEvento
                ? formatarDataPtBR(ev.dataEvento)
                : "";

              const eventoPassado =
                temCalendario && isDataEventoPassada(ev.dataEvento);
              const temDestaque = !!ev.destaque;

              const turmasEv = getTurmasDoEvento(ev);

              // Botão Editar só faz sentido em evento de calendário (destaque não
              // edita). Bloqueio por permissão é da página inteira (ver overlay).
              const podeEditar = !!ev.calendario;
              const turmasNomes = (turmasEv || []).map((id) => ({
                id: String(id),
                nome: labelTurma(disciplinasById.get(String(id)), id),
              }));

              const turmasOrdenadas = [...turmasNomes].sort(
                (a, b) => a.id - b.id,
              );
              const mostrarTodasTurmas = isCoordenador;

              if (isCoordenador) {
                return (
                  <CardCoordenador
                    key={ev.id}
                    ev={ev}
                    criadoPor={criadoPor}
                    dataAtual={dataAtual}
                    temDataEvento={temDataEvento}
                    dataEventoFmt={dataEventoFmt}
                    podeEditar={podeEditar}
                    eventoPassado={eventoPassado}
                    handleEditar={handleEditar}
                    temCalendario={temCalendario}
                    temDestaque={temDestaque}
                    turmasOrdenadas={turmasOrdenadas}
                    mostrarTodasTurmas={mostrarTodasTurmas}
                    temConfirmacaoPresenca={!!ev.confirmacaoPresenca}
                    onVerConfirmacoes={() => setEventoConfirmacoesId(ev.id)}
                  />
                );
              }

              const podeVerTurmas = !isAluno && !isResponsavel;

              return (
                <CardDefault
                  key={ev.id}
                  ev={ev}
                  criadoPor={criadoPor}
                  dataAtual={dataAtual}
                  temDataEvento={temDataEvento}
                  dataEventoFmt={dataEventoFmt}
                  podeEditar={podeEditar}
                  eventoPassado={eventoPassado}
                  handleEditar={handleEditar}
                  temCalendario={temCalendario}
                  temDestaque={temDestaque}
                  turmasOrdenadas={podeVerTurmas ? turmasOrdenadas : []}
                  temConfirmacaoPresenca={!!ev.confirmacaoPresenca}
                  onVerConfirmacoes={() => setEventoConfirmacoesId(ev.id)}
                />
              );
            })}

            {!eventosFiltrados.length && (
              <div className="eventos-publicados-empty">
                Nenhum evento publicado para o filtro selecionado.
              </div>
            )}
          </div>
        </div>
        <ModalConfirmacoes
          aberto={!!eventoConfirmacoesId}
          eventoId={eventoConfirmacoesId}
          onFechar={() => setEventoConfirmacoesId(null)}
        />
      </div>
    </div>
  );
}
